
  import { defineComponent, PropType } from 'vue';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import security from '@/common/mixins/security';

  import { splitCodes } from '@/modules/ved/utils/fts/code';

  import { CODE_SEPARATORS } from '../../../constants/fts/codes';
  import { TFTSMonthlyFilter, TFTSMonthlyFilterRequest } from '../../../types/fts-monthly';
  import { TExportConfig } from '../../common/ved-aggregated-export-buttons/types';
  import FTSFilterWrapper from '../fts-filter/FTSFilterWrapper.vue';
  import FTSFilterWrapperToogle from '../fts-filter/FTSFilterWrapperToogle.vue';
  import FTSAdditionalUnitsField from '../FTSAdditionalUnitsField.vue';
  import FTSCountriesFilter from '../FTSCountriesFilter.vue';
  import FTSCumulativeTotalField from '../FTSCumulativeTotalField.vue';
  import FTSCustomProcedureField from '../FTSCustomProcedureField.vue';
  import FTSIndicatorTypeField from '../FTSIndicatorTypeField.vue';
  import FTSOkatoFilter from '../FTSOkatoFilter.vue';
  import FTSPriceField from '../FTSPriceField.vue';
  import FTSProductsFilter from '../FTSProductsFilter.vue';
  import FTSSectionField from '../FTSSectionField.vue';
  import FTSTradeDirectionField from '../FTSTradeDirectionField.vue';
  import FTSWeightField from '../FTSWeightField.vue';
  import FTSMonthlyPeriodField from './FTSMonthlyPeriodField.vue';

  const getPreparedFilter = (
    currentFilter: Partial<TFTSMonthlyFilter> | undefined,
    requestFilter: TFTSMonthlyFilterRequest,
  ): TFTSMonthlyFilter => {
    const merged = {
      ...(currentFilter ?? {}),
      ...requestFilter,
      codes: requestFilter.codes.join(CODE_SEPARATORS),
    };

    return {
      ...merged,
      isMoreCost: merged.isMoreCost ?? false,
    };
  };

  export default defineComponent({
    name: 'FTSMonthlyFilter',
    components: {
      FTSProductsFilter,
      FTSCountriesFilter,
      FTSFilterWrapper,
      FTSOkatoFilter,
      FTSMonthlyPeriodField,
      FTSCustomProcedureField,
      ComponentInput,
      FTSTradeDirectionField,
      FTSIndicatorTypeField,
      FTSWeightField,
      FTSPriceField,
      FTSSectionField,
      FTSFilterWrapperToogle,
      FTSCumulativeTotalField,
      FTSAdditionalUnitsField,
    },
    emits: {
      searchFilter: (filter: TFTSMonthlyFilterRequest) => filter,
      updateFilter: (filter: TFTSMonthlyFilterRequest) => filter,
    },
    mixins: [security],
    props: {
      defaultFilter: {
        type: Object as PropType<TFTSMonthlyFilterRequest>,
        required: true,
      },
      readonlyPermissionCode: {
        type: Number,
      },
      exportConfig: {
        type: Object as PropType<TExportConfig>,
        required: true,
      },
    },
    data() {
      return {
        filter: getPreparedFilter(undefined, this.defaultFilter),
        isReadonly: true,
      };
    },
    methods: {
      prepareFilterForExport(filter: TFTSMonthlyFilter): TFTSMonthlyFilterRequest {
        const filterCopy: Omit<TFTSMonthlyFilter, 'isMoreCost'> & {
          isMoreCost?: boolean;
        } = { ...filter };

        if (!filterCopy.isMoreCost) {
          delete filterCopy.moreCost;
        }

        delete filterCopy.isMoreCost;

        return {
          ...filterCopy,
          codes: splitCodes(filter.codes),
        };
      },
      searchFilter(filter: TFTSMonthlyFilter) {
        this.initFilter(filter);

        this.$emit('searchFilter', this.prepareFilterForExport(filter));
      },
      initFilter(filter: TFTSMonthlyFilter) {
        this.filter = { ...filter };
      },
      loadFilter(loaded: TFTSMonthlyFilter) {
        this.filter = { ...getPreparedFilter(this.filter, this.defaultFilter), ...loaded };
      },
    },
    watch: {
      defaultFilter: {
        handler(newValue: TFTSMonthlyFilterRequest) {
          this.filter = getPreparedFilter(this.filter, newValue);
        },
        immediate: true,
        deep: true,
      },
      filter: {
        handler(updatedFilter: TFTSMonthlyFilter) {
          this.$emit('updateFilter', this.prepareFilterForExport(updatedFilter));
        },
        deep: true,
      },
      readonlyPermissionCode: {
        async handler(newValue: number) {
          if (newValue) {
            this.isReadonly = !(await this.checkPermission(newValue)).data;
          } else {
            this.isReadonly = false;
          }
        },
        immediate: true,
      },
    },
  });
